/**
 * chartRenderer.ts
 *
 * Pure-function, dependency-free SVG chart generator for report scripts (see
 * reportRenderer.ts's generateReportSectionHtml, which exposes renderChart() into its
 * sandboxed script context). Report scripts have no React renderer and no DOM available —
 * they just build an HTML string — so charts here are plain SVG markup, not a React component
 * tree. This intentionally avoids recharts/react-dom (used by GrowthLineChart.web.tsx /
 * DevAgeLineChart.web.tsx) since those are dependencies of packages/web only; packages/shared
 * also ships to native, where neither exists. The coordinate math below (margin box, per-axis
 * scale functions, polyline/gridline/label construction) mirrors GrowthLineChart.tsx's own
 * native (react-native-svg) implementation, just targeting a plain string template instead of
 * react-native-svg components.
 *
 * Output is always a self-contained inline-styled block — no external CSS class dependency,
 * no <script> tags, nothing that needs to execute once loaded — safe to drop into any WebView/
 * iframe (native react-native-webview or the web WebViewShim) or a PDF export. The outer <svg>
 * always carries explicit width/height attributes (not just a viewBox) because the WebView
 * auto-height mechanisms on both platforms measure actual rendered box size, not intrinsic SVG
 * content — a percentage-width/no-height SVG can collapse to zero height in that environment.
 */

export type ChartType = 'bar' | 'line';

export interface ChartSeries {
  /** Legend label. Omit for a single, unlabeled implicit series (no legend shown). */
  label?: string;
  /** Coerced via Number(v) || 0 — getData()/calculateScore() return strings/numbers; report
   * authors shouldn't have to cast by hand. */
  values: (number | string)[];
  /** Defaults to the next color in DEFAULT_PALETTE, round-robin by series index. */
  color?: string;
}

export interface ChartConfig {
  type: ChartType;
  /** Shared x-axis — one label per data point/category, across every series. */
  labels: string[];
  /** Single-series shorthand (both chart types). Exactly one of values/series should be
   * given; if both are given, series wins. */
  values?: (number | string)[];
  /** Multi-series — line charts only in this pass. Bar charts always read series[0] and
   * reject (with a visible inline error) any call with more than one series, since grouped/
   * stacked bar layout isn't built. */
  series?: ChartSeries[];
  title?: string;
  /** Default 500. */
  width?: number;
  /** Default 260 (line charts render slightly taller when a legend is shown, handled
   * internally — this is the requested chart+axis height, legend adds to it). */
  height?: number;
  /** Single-series bar fill / line stroke override. Ignored when series[] is given — set
   * color per-series instead. */
  color?: string;
  /** Explicit y-axis bounds. Auto-computed from the data when omitted: bar charts default to
   * a zero baseline (min(0, data min) .. data max); line charts default to the data's own
   * min/max across every series, padded ~10% so points don't sit exactly on the plot edge. */
  yMin?: number;
  yMax?: number;
  /** Appended to every value label, e.g. '%'. */
  valueSuffix?: string;
  /** Per-bar/point value labels. Default true; automatically off for multi-series line charts
   * (series.length > 1) to avoid overlapping label clutter — still overridable. */
  showValues?: boolean;
  /** Horizontal gridlines, one per y-axis tick. Default true. Tick *labels* (in the left
   * margin) always show regardless of this — showGrid only toggles the lines themselves. */
  showGrid?: boolean;
  /** Explicit spacing between y-axis ticks (e.g. 25 for 0/25/50/75/100). Takes priority over
   * yTickCount when both are given. Tick values always include yMin/yMax's actual bounds via
   * the nearest step multiples — see computeYTicks. */
  yTickStep?: number;
  /** Number of evenly-spaced y-axis ticks (including both endpoints) when yTickStep isn't
   * given. Default 5. */
  yTickCount?: number;
}

const DEFAULT_PALETTE = ['#3498db', '#e67e22', '#27ae60', '#9b59b6', '#e74c3c', '#16a085'];
const FONT = 'Arial, sans-serif';

const escapeHtml = (s: string): string =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const coerceNum = (v: number | string): number => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Rounds to at most 1 decimal place, dropping a trailing ".0". */
const formatValue = (v: number, suffix?: string): string => {
  const rounded = Math.round(v * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return suffix ? `${text}${suffix}` : text;
};

const scaleValue = (v: number, yMin: number, yMax: number, plotH: number): number =>
  yMax === yMin ? plotH / 2 : plotH - ((v - yMin) / (yMax - yMin)) * plotH;

/** Visible inline error block — matches generateReportSectionHtml's own existing
 * error-paragraph convention, so one bad chart call degrades gracefully instead of throwing
 * and blanking the whole section's output. */
const renderChartError = (message: string): string =>
  `<div style="text-align:center;margin:12px 0;"><p style="color:#e74c3c;font-family:${FONT};font-size:13px;border:1px solid #e74c3c;border-radius:6px;padding:10px;display:inline-block;">Chart error: ${escapeHtml(message)}</p></div>`;

interface NormSeries {
  label?: string;
  values: number[];
  color: string;
}

function normalizeSeries(config: ChartConfig): { series: NormSeries[] } | { error: string } {
  const hasValues = Array.isArray(config.values);
  const hasSeries = Array.isArray(config.series) && config.series!.length > 0;
  if (!hasValues && !hasSeries) {
    return { error: 'renderChart requires either a "values" array or a "series" array.' };
  }
  const rawSeries = hasSeries ? config.series! : [{ values: config.values! }];
  for (const s of rawSeries) {
    if (!Array.isArray(s.values) || s.values.length !== config.labels.length) {
      return {
        error: `Series "${s.label ?? ''}" has ${s.values?.length ?? 0} value(s) but there ${
          config.labels.length === 1 ? 'is' : 'are'
        } ${config.labels.length} label(s) — they must match.`,
      };
    }
  }
  return {
    series: rawSeries.map((s, i) => ({
      label: s.label,
      values: s.values.map(coerceNum),
      color: s.color || DEFAULT_PALETTE[i % DEFAULT_PALETTE.length],
    })),
  };
}

function computeMargins(hasTitle: boolean, hasLegend: boolean) {
  return {
    top: hasTitle ? 36 : 16,
    right: 16,
    bottom: 44,
    left: 44,
    legendHeight: hasLegend ? 26 : 0,
  };
}

/** Returns actual y-axis tick *values* (not fractions) spanning [yMin, yMax] — either every
 * multiple of config.yTickStep that falls in range, or config.yTickCount (default 5) evenly
 * spaced values including both endpoints. Always includes at least yMin and yMax so the axis
 * never implies a range narrower than the data actually spans. */
function computeYTicks(yMin: number, yMax: number, config: ChartConfig): number[] {
  if (yMax === yMin) return [yMin];

  if (config.yTickStep && config.yTickStep > 0) {
    const step = config.yTickStep;
    const ticks: number[] = [];
    const start = Math.ceil(yMin / step) * step;
    for (let t = start; t <= yMax + step * 1e-6; t += step) {
      ticks.push(Math.round(t * 1e6) / 1e6); // guard against float drift (e.g. 0.30000000004)
    }
    if (ticks.length === 0 || ticks[0] > yMin) ticks.unshift(yMin);
    if (ticks[ticks.length - 1] < yMax) ticks.push(yMax);
    return ticks;
  }

  const count = Math.max(2, config.yTickCount ?? 5);
  return Array.from({ length: count }, (_, i) => yMin + ((yMax - yMin) * i) / (count - 1));
}

/** Renders one gridline (when showGrid) + one left-margin value label per y-axis tick — the
 * "simple y-axis" itself. Ticks are positioned via the same scaleValue() every data point/bar
 * uses, so gridlines/labels always line up exactly with the plotted data. */
function yAxisSvg(
  ticks: number[],
  yMin: number,
  yMax: number,
  plotW: number,
  plotH: number,
  showGrid: boolean,
  valueSuffix: string | undefined
): string {
  return ticks
    .map(t => {
      const y = scaleValue(t, yMin, yMax, plotH);
      const grid = showGrid
        ? `<line x1="0" y1="${y}" x2="${plotW}" y2="${y}" stroke="#e0e0e0" stroke-width="1" />`
        : '';
      const label = `<text x="-8" y="${y + 4}" text-anchor="end" font-size="10" fill="#7f8c8d" font-family="${FONT}">${escapeHtml(formatValue(t, valueSuffix))}</text>`;
      return grid + label;
    })
    .join('');
}

function renderBarChartSvg(config: ChartConfig, series: NormSeries[]): string {
  const width = config.width ?? 500;
  const height = config.height ?? 260;
  const showValues = config.showValues !== false;
  const showGrid = config.showGrid !== false;
  const s = series[0];

  const margin = computeMargins(!!config.title, false);
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;

  const dataMin = Math.min(0, ...s.values);
  const dataMax = Math.max(...s.values);
  const yMin = config.yMin ?? dataMin;
  const yMax = config.yMax ?? (dataMax === yMin ? yMin + 1 : dataMax);

  const n = config.labels.length;
  const slotW = plotW / n;
  const barW = Math.max(4, slotW * 0.6);
  const barGap = (slotW - barW) / 2;
  const zeroY = scaleValue(0, yMin, yMax, plotH);
  const yTicks = computeYTicks(yMin, yMax, config);

  const bars = s.values
    .map((v, i) => {
      const x = i * slotW + barGap;
      const yTop = scaleValue(Math.max(v, 0), yMin, yMax, plotH);
      const yBottom = scaleValue(Math.min(v, 0), yMin, yMax, plotH);
      const barHeight = Math.max(0, yBottom - yTop);
      const valueLabel = showValues
        ? `<text x="${x + barW / 2}" y="${yTop - 4}" text-anchor="middle" font-size="11" fill="#2c3e50" font-family="${FONT}">${escapeHtml(formatValue(v, config.valueSuffix))}</text>`
        : '';
      const xLabel = `<text x="${x + barW / 2}" y="${plotH + 18}" text-anchor="middle" font-size="11" fill="#7f8c8d" font-family="${FONT}">${escapeHtml(config.labels[i])}</text>`;
      return `<rect x="${x}" y="${yTop}" width="${barW}" height="${barHeight}" fill="${s.color}" rx="2" />${valueLabel}${xLabel}`;
    })
    .join('');

  const titleSvg = config.title
    ? `<text x="${width / 2}" y="20" text-anchor="middle" font-size="14" font-weight="bold" fill="#2c3e50" font-family="${FONT}">${escapeHtml(config.title)}</text>`
    : '';

  return `<div style="text-align:center;margin:12px 0;">
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
${titleSvg}
<g transform="translate(${margin.left},${margin.top})">
${yAxisSvg(yTicks, yMin, yMax, plotW, plotH, showGrid, config.valueSuffix)}
<line x1="0" y1="${zeroY}" x2="${plotW}" y2="${zeroY}" stroke="#ccc" stroke-width="1" />
${bars}
</g>
</svg>
</div>`;
}

function renderLineChartSvg(config: ChartConfig, series: NormSeries[]): string {
  const width = config.width ?? 500;
  const requestedHeight = config.height ?? 260;
  const hasLegend = series.length > 1 || (series.length === 1 && !!series[0].label);
  // Off by default once there's more than one line — per-point value labels overlap badly
  // with several series sharing the same plot; still explicitly overridable.
  const showValues = config.showValues !== undefined ? config.showValues : series.length === 1;
  const showGrid = config.showGrid !== false;

  const margin = computeMargins(!!config.title, hasLegend);
  const svgHeight = requestedHeight; // legend renders as plain HTML below the <svg>, not inside it
  const plotW = width - margin.left - margin.right;
  const plotH = svgHeight - margin.top - margin.bottom;

  const allValues = series.flatMap(sr => sr.values);
  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);
  const pad = (dataMax - dataMin) * 0.1 || 1; // flat data (all-equal values) still gets breathing room
  const yMin = config.yMin ?? dataMin - pad;
  const yMax = config.yMax ?? dataMax + pad;
  const yTicks = computeYTicks(yMin, yMax, config);

  const n = config.labels.length;
  const scaleX = (i: number) => (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);

  const seriesSvg = series
    .map(sr => {
      const points = sr.values.map((v, i) => `${scaleX(i)},${scaleValue(v, yMin, yMax, plotH)}`).join(' ');
      const dots = sr.values
        .map((v, i) => {
          const cx = scaleX(i);
          const cy = scaleValue(v, yMin, yMax, plotH);
          const valueLabel = showValues
            ? `<text x="${cx}" y="${cy - 8}" text-anchor="middle" font-size="11" fill="#2c3e50" font-family="${FONT}">${escapeHtml(formatValue(v, config.valueSuffix))}</text>`
            : '';
          return `<circle cx="${cx}" cy="${cy}" r="4" fill="${sr.color}" stroke="#fff" stroke-width="1.5" />${valueLabel}`;
        })
        .join('');
      return `<polyline points="${points}" fill="none" stroke="${sr.color}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />${dots}`;
    })
    .join('');

  const labelStep = Math.max(1, Math.ceil(n / 6));
  const xLabelsSvg = config.labels
    .map((lbl, i) => {
      if (i % labelStep !== 0 && i !== n - 1) return '';
      return `<text x="${scaleX(i)}" y="${plotH + 18}" text-anchor="middle" font-size="11" fill="#7f8c8d" font-family="${FONT}">${escapeHtml(lbl)}</text>`;
    })
    .join('');

  const titleSvg = config.title
    ? `<text x="${width / 2}" y="20" text-anchor="middle" font-size="14" font-weight="bold" fill="#2c3e50" font-family="${FONT}">${escapeHtml(config.title)}</text>`
    : '';

  const legendHtml = hasLegend
    ? `<div style="display:flex;justify-content:center;flex-wrap:wrap;gap:14px;margin-top:4px;">${series
        .map(
          sr =>
            `<span style="display:inline-flex;align-items:center;gap:5px;font-size:12px;color:#555;font-family:${FONT};"><span style="display:inline-block;width:10px;height:10px;border-radius:5px;background:${sr.color};"></span>${escapeHtml(sr.label || '')}</span>`
        )
        .join('')}</div>`
    : '';

  return `<div style="text-align:center;margin:12px 0;">
<svg width="${width}" height="${svgHeight}" viewBox="0 0 ${width} ${svgHeight}" xmlns="http://www.w3.org/2000/svg">
${titleSvg}
<g transform="translate(${margin.left},${margin.top})">
${yAxisSvg(yTicks, yMin, yMax, plotW, plotH, showGrid, config.valueSuffix)}
<line x1="0" y1="${plotH}" x2="${plotW}" y2="${plotH}" stroke="#ccc" stroke-width="1" />
${seriesSvg}
${xLabelsSvg}
</g>
</svg>
${legendHtml}
</div>`;
}

/**
 * Renders a bar or line chart to a self-contained HTML string (a <div> wrapping an inline
 * <svg>, plus a legend row for multi-series line charts) — see ChartConfig for the full
 * config shape. Never throws: a missing/malformed config, mismatched labels/values lengths,
 * an unknown type, or a bar chart given more than one series all produce a visible inline
 * error block instead, so one bad renderChart() call can't blank out an entire report section.
 */
export function renderChart(config: ChartConfig): string {
  try {
    if (!config || (config.type !== 'bar' && config.type !== 'line')) {
      return renderChartError(`unknown chart type "${config?.type}" — must be "bar" or "line".`);
    }
    if (!Array.isArray(config.labels) || config.labels.length === 0) {
      return renderChartError('renderChart requires a non-empty "labels" array.');
    }

    const normalized = normalizeSeries(config);
    if ('error' in normalized) {
      return renderChartError(normalized.error);
    }

    if (config.type === 'bar' && normalized.series.length > 1) {
      return renderChartError(
        'Multi-series bar charts are not supported yet — pass a single "values" array (or one series) for bar charts. Use type "line" for multi-series data.'
      );
    }

    return config.type === 'bar'
      ? renderBarChartSvg(config, normalized.series)
      : renderLineChartSvg(config, normalized.series);
  } catch (error) {
    return renderChartError(error instanceof Error ? error.message : String(error));
  }
}
